import { Suspense, useCallback, useEffect, useState, type ReactNode } from 'react';
import type { SignedIn } from './api.ts';
import { FrameSlot, StaffFrame, pageArea, type FramedPage, type StaffContext } from './frame.tsx';
import { useMoving } from './router.ts';
import { strings } from './strings.ts';

/** How long a move may take before the page area shows the skeleton: a quicker one shows no waiting at all. */
const SKELETON_AFTER_MS = 400;

/**
 * The staff frame held across staff pages (app.tsx): moving between them
 * swaps only the page under it, so the sidebar, the top bar and the tab bar
 * stay on screen. Each page's Screen tells it who and where (its `staff`)
 * and its kind; until the next page has read the group's face and outlet,
 * the frame keeps the ones it showed. A move is a transition (place.tsx):
 * the page moved from stays until the next one has its file, and only a
 * move slower than SKELETON_AFTER_MS shows the page's skeleton in its place.
 * The first page of a visit shows the skeleton while its file loads.
 */
export function StaffLayout({ who, onShown, children }: { readonly who: SignedIn; /** Once, when the first page is on screen: the time to fetch what the next moves will need. */ readonly onShown?: () => void; readonly children: ReactNode }) {
  const [page, setPage] = useState<FramedPage | null>(null);
  const shown = page !== null;
  useEffect(() => {
    if (shown) onShown?.();
  }, [shown, onShown]);
  const tell = useCallback((next: FramedPage) => {
    setPage((last) => {
      const group = next.staff.group ?? last?.staff.group ?? null;
      const outlet = next.staff.outlet ?? last?.staff.outlet ?? null;
      if (last !== null && last.kind === next.kind && last.staff.group === group && last.staff.outlet === outlet && last.staff.onCurrent === next.staff.onCurrent) return last;
      return { kind: next.kind, staff: { ...next.staff, group, outlet } };
    });
  }, []);
  const slow = useSlowMove();
  const staff: StaffContext = { ...page?.staff, groupId: who.groupId, who, group: page?.staff.group ?? null };
  return (
    <FrameSlot.Provider value={tell}>
      <StaffFrame staff={staff} kind={page?.kind ?? 'office'}>
        {slow && <PageSkeleton />}
        <div className={slow ? 'hidden' : 'contents'}>
          <Suspense fallback={<PageSkeleton />}>{children}</Suspense>
        </div>
      </StaffFrame>
    </FrameSlot.Provider>
  );
}

/** Whether the move on its way has taken longer than SKELETON_AFTER_MS. */
function useSlowMove(): boolean {
  const moving = useMoving();
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (!moving) {
      setSlow(false);
      return;
    }
    const timer = setTimeout(() => {
      setSlow(true);
    }, SKELETON_AFTER_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [moving]);
  return slow && moving;
}

const BONE = 'rounded-control bg-sunk animate-pulse motion-reduce:animate-none';

/** A staff page's shape while it loads: its title, its lead, and a card of rows. Busy for a screen reader, which hears the one word. */
function PageSkeleton() {
  return (
    <main aria-busy="true" className={`${pageArea(true)} animate-fade-in motion-reduce:animate-none`}>
      <span className="sr-only" role="status">
        {strings.common.loading}
      </span>
      <div aria-hidden="true" className="flex flex-col gap-s3">
        <div className={`h-s6 w-2/5 max-w-[20rem] ${BONE}`} />
        <div className={`h-s4 w-3/5 max-w-[32rem] ${BONE}`} />
      </div>
      <div aria-hidden="true" className="flex flex-col gap-s4 rounded-card border border-line bg-surface p-s5">
        {[0, 1, 2, 3].map((row) => (
          <div key={row} className="flex items-center gap-s4">
            <div className={`size-s6 shrink-0 rounded-chip ${BONE}`} />
            <div className={`h-s4 flex-1 ${BONE}`} />
            <div className={`h-s4 w-1/5 ${BONE}`} />
          </div>
        ))}
      </div>
    </main>
  );
}
